/**
 * Shared image rehydration for state-restore paths (fullState + essProjectLoaded).
 *
 * Restore payloads are image-stripped (only imageId, no base64) to keep them
 * small. After applying such a payload, the referenced image blobs must be
 * rehydrated: served from the IndexedDB cache where possible, fetched from the
 * server for the rest. Both the reconnect (fullState) and project-load
 * (essProjectLoaded) paths need identical behavior, so it lives here.
 */

import type { Socket } from "socket.io-client";
import type { ServerToClientEvents, ClientToServerEvents } from "@amzn/a4k-creator-studio-shared";
import { useStoryStore } from "../storyStore";
import { getCachedImages } from "../../lib/imageCache";

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/** Find the story entity (character, scene, or beat) with a matching imageId and
 *  set its imageBase64. Comic pages are intentionally excluded — their images
 *  are resolved on demand through the bounded image loader, not pinned inline. */
export function applyImageToStores(imageId: string, base64: string): void {
  const storyState = useStoryStore.getState();

  for (const c of storyState.characters) {
    if (c.imageId === imageId) {
      useStoryStore.getState().updateCharacter(c.characterId, { imageBase64: base64 });
      return;
    }
  }
  for (const s of storyState.scenes) {
    if (s.imageId === imageId) {
      useStoryStore.getState().updateScene(s.sceneId, { imageBase64: base64 });
      return;
    }
  }
  for (const b of storyState.beats) {
    if (b.imageId === imageId) {
      useStoryStore.getState().updateBeat(b.beatId, { imageBase64: base64 });
      return;
    }
  }
}

/** Rehydrate a set of imageIds: apply cache hits, fetch the rest from the server
 *  (which replies via the `imageData` event). No-op for an empty list. */
export function rehydrateImages(socket: TypedSocket, imageIds: string[]): void {
  if (imageIds.length === 0) return;
  getCachedImages(imageIds).then((cached) => {
    for (const [imageId, base64] of cached) {
      applyImageToStores(imageId, base64);
    }
    const missing = imageIds.filter((id) => !cached.has(id));
    if (missing.length > 0) {
      socket.emit("fetchImages", { imageIds: missing });
    }
  }).catch(() => {
    // Cache lookup failed — fetch everything from the server.
    socket.emit("fetchImages", { imageIds });
  });
}
